import { useState, useEffect } from "react";
import { Navigation, Footer, ScriptureBlock } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { Heart, CheckCircle } from "lucide-react";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Prayer() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prayers, setPrayers] = useState([]);
  const [loadingPrayers, setLoadingPrayers] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    request_text: "",
    is_anonymous: false,
    not_substitute_confirmed: false
  });

  useEffect(() => {
    fetchPrayers();
  }, []);

  const fetchPrayers = async () => {
    try {
      const response = await axios.get(`${API}/prayer-requests?limit=10`);
      setPrayers(response.data.prayer_requests || []);
    } catch (error) {
      console.error("Failed to fetch prayers:", error);
    } finally {
      setLoadingPrayers(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.not_substitute_confirmed) {
      toast.error("Please confirm the checkbox to proceed.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/prayer-requests`, formData);
      setSubmitted(true);
      toast.success("Prayer request submitted!");
      fetchPrayers(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setFormData({
      name: "",
      email: "",
      request_text: "",
      is_anonymous: false,
      not_substitute_confirmed: false
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="prayer-page">
      <SEO 
        title="Prayer Requests"
        description="Submit prayer requests to Steward Lots. We believe in the power of prayer and are committed to praying for our community."
        path="/prayer"
      />
      <Navigation mode="builder" />

      {/* Hero */}
      <section className="pt-24 pb-16 px-6 md:px-12 bg-[#0F0F10]" data-testid="prayer-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-6">
            Prayer & Community
          </h1>
          <p className="text-[#A8A29E] text-lg max-w-2xl mx-auto">
            We believe in the power of prayer. Share your requests and know that we are praying with you.
          </p>
        </div>
      </section>

      <div className="py-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Prayer Form */}
            <div>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6">
                Submit a Prayer Request
              </h2>

              {submitted ? (
                <div className="bg-[#F5F5F4] p-8 border border-[#E7E5E4] text-center" data-testid="prayer-success">
                  <div className="w-16 h-16 bg-[#0F0F10] rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-serif text-xl text-[#1C1917] mb-4">
                    We Are Praying
                  </h3>
                  <p className="text-[#57534E] mb-6">
                    Your prayer request has been received. We lift you up before the Lord.
                  </p>
                  <ScriptureBlock
                    verse="Confess your faults one to another, and pray one for another, that ye may be healed."
                    reference="James 5:16"
                    className="inline-block text-left mb-6"
                  />
                  <Button onClick={resetForm} variant="outline" data-testid="submit-another-btn">
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="prayer-form">
                  {/* Anonymous Toggle */}
                  <div className="flex items-center justify-between p-4 bg-[#F5F5F4] border border-[#E7E5E4]">
                    <Label htmlFor="is_anonymous" className="cursor-pointer">
                      Submit anonymously
                    </Label>
                    <Switch
                      id="is_anonymous"
                      checked={formData.is_anonymous}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_anonymous: checked }))}
                      data-testid="switch-anonymous"
                    />
                  </div>

                  {!formData.is_anonymous && (
                    <>
                      <div>
                        <Label htmlFor="name">Name</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name (optional if not anonymous)"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email (Optional)</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="We won't share this"
                          data-testid="input-email"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <Label htmlFor="request_text">Prayer Request *</Label>
                    <Textarea
                      id="request_text"
                      name="request_text"
                      value={formData.request_text}
                      onChange={handleChange}
                      placeholder="Share your prayer request..."
                      rows={5}
                      required
                      data-testid="input-request"
                    />
                  </div>

                  {/* Confirmation */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="not_substitute_confirmed"
                      checked={formData.not_substitute_confirmed}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, not_substitute_confirmed: checked }))}
                      data-testid="checkbox-confirm"
                    />
                    <Label htmlFor="not_substitute_confirmed" className="text-sm cursor-pointer text-[#57534E]">
                      I understand this is not a substitute for professional help or medical advice. *
                    </Label>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-[#0F0F10] hover:bg-[#1C1917]"
                    disabled={loading}
                    data-testid="submit-prayer-btn"
                  >
                    {loading ? "Submitting..." : "Submit Prayer Request"}
                  </Button>
                </form>
              )}
            </div>

            {/* Recent Prayers */}
            <div>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6">
                Recent Prayer Requests
              </h2>
              <p className="text-[#57534E] text-sm mb-6">
                Join us in praying for these requests.
              </p>

              {loadingPrayers ? (
                <div className="text-center py-8 text-[#A8A29E]">Loading...</div>
              ) : prayers.length === 0 ? (
                <div className="bg-[#F5F5F4] p-8 border border-[#E7E5E4] text-center">
                  <Heart className="w-8 h-8 text-[#BFA15F] mx-auto mb-4" />
                  <p className="text-[#57534E]">No prayer requests yet. Be the first to share.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="prayer-list">
                  {prayers.map((prayer, index) => (
                    <div 
                      key={prayer.id || index}
                      className="bg-[#F5F5F4] p-6 border border-[#E7E5E4]"
                      data-testid={`prayer-item-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <Heart className="w-5 h-5 text-[#BFA15F] flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-[#57534E] mb-2">{prayer.request_text}</p>
                          <p className="text-xs text-[#A8A29E]">
                            — {prayer.name || "Anonymous"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 p-6 bg-[#0F0F10] text-white">
                <ScriptureBlock
                  verse="Bear ye one another's burdens, and so fulfil the law of Christ."
                  reference="Galatians 6:2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
